import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Divider,
  Grid,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Paper,
  useTheme,
  ButtonBase,
  Card,
  CardContent,
  CardActions,
  Chip,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";
import {
  formatAge,
  formatPhone,
  formatCpfCnpj,
  unformatCpfCnpj,
} from "../utils";
import {
  Close,
  Person,
  Add,
  Store,
  Pets,
  Edit,
  CloudUpload,
  Delete,
  Download,
  Image,
  AttachFile,
  Visibility,
  PictureAsPdf,
  OpenInNew,
  ExpandMore,
  NotificationsActive,
  CheckCircle,
} from "@mui/icons-material";
import { IPetPopulated, IUser, IPedido, INotification } from "../types";
import {
  getPedidosByUser,
  updatePet,
  deletePet,
  uploadImage,
  createOrUpdatePetHistory,
  getLatestPetHistory,
  IPetHistoryData,
  createNotification,
  updateTutor,
  getNotifications,
  markNotificationAsRead,
} from "../services";
import { format } from "date-fns";
import { normalizePetUpdatePayload } from "../utils";
// import EmptyState from "./EmptyState";
// import VendaCard from "./VendasCard";
import ModalNewAtendimento from "./ModalNewAtendimento";
import ModalNewVenda from "./ModalNewVenda";
import ModalDetalhesPedido from "./ModalDetalhesPedido";
import ModalEditarPedido from "./ModalEditarPedido";

// ====== HELPERS: Pedido (Atendimento) Date Parsing & Ordering ======
// Alguns campos possíveis de data retornados pela API: Data, DataEnvio, DataAbertura (em Ordem de Serviço), DataAprovacaoPedido.
// Agora priorizamos o campo Codigo (sequencial) para ordenação, usando datas apenas como fallback.
// Também protegemos contra formatos brasileiros (dd/MM/yyyy ou dd/MM/yyyy HH:mm:ss) caso venham assim.
const extractPedidoTimestamp = (pedido: IPedido): number => {
  const raw =
    pedido.Data ||
    (pedido as any).DataEnvio ||
    (pedido as any).DataAbertura ||
    (pedido as any).DataAprovacaoPedido ||
    "";
  if (!raw) return 0;

  // 1. Tenta parse nativo (ISO ou formatos reconhecidos)
  const native = Date.parse(raw);
  if (!isNaN(native)) return native;

  // 2. Tenta formato BR dd/MM/yyyy [HH:mm[:ss]]
  if (raw.includes("/")) {
    try {
      const [datePart, timePart] = raw.split(" ");
      const [dd, mm, yyyy] = datePart.split("/");
      if (dd && mm && yyyy) {
        const hours = timePart?.split(":")[0];
        const minutes = timePart?.split(":")[1];
        const seconds = timePart?.split(":")[2];
        const d = new Date(
          Number(yyyy),
          Number(mm) - 1,
          Number(dd),
          hours ? Number(hours) : 0,
          minutes ? Number(minutes) : 0,
          seconds ? Number(seconds) : 0
        );
        if (!isNaN(d.getTime())) return d.getTime();
      }
    } catch {
      // silenciosamente ignora e cai no retorno 0
    }
  }
  return 0; // fallback: datas inválidas vão para o fim ao ordenar desc
};

const sortPedidosDesc = (a: IPedido, b: IPedido) => {
  // 1. Ordenação primária: Codigo desc (mais recente = maior número)
  const codeA = (a as any).Codigo;
  const codeB = (b as any).Codigo;
  const nA = typeof codeA === "number" ? codeA : parseInt(codeA, 10);
  const nB = typeof codeB === "number" ? codeB : parseInt(codeB, 10);
  if (!isNaN(nA) && !isNaN(nB) && nA !== nB) {
    return nB - nA;
  }
  // 2. Fallback: datas (mais recente primeiro)
  return extractPedidoTimestamp(b) - extractPedidoTimestamp(a);
};

// Função para formatar data sem horário (apenas DD/MM/YYYY)
const formatDateOnly = (
  dateValue: string | Date | null | undefined
): string => {
  if (!dateValue) return "Não informado";

  try {
    let date: Date;

    // Se já for um objeto Date
    if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      // Se for string, tentar diferentes formatos
      const dateStr = String(dateValue).trim();

      // Formato brasileiro: dd/MM/yyyy ou dd/MM/yyyy HH:mm:ss
      if (dateStr.includes("/")) {
        const parts = dateStr.split(/[\s\/]/);
        if (parts.length >= 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexed
          const year = parseInt(parts[2], 10);
          date = new Date(year, month, day);
        } else {
          date = new Date(dateStr);
        }
      } else {
        // Formato ISO ou outro
        date = new Date(dateStr);
      }
    }

    if (isNaN(date.getTime())) return "Não informado";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return "Não informado";
  }
};

// Função para calcular próxima data de aplicação (adiciona 30 dias por padrão)
const calculateNextDate = (
  lastDate: string,
  daysToAdd: number = 30
): string => {
  if (!lastDate) return "";

  try {
    const date = new Date(lastDate);
    if (isNaN(date.getTime())) return "";

    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

interface PetDetailsProps {
  initialData: IPetPopulated | null;
  onClose?: () => void;
  onSave?: () => void;
  open?: boolean;
  tecnico: IUser | null;
  openModalAddPet?: () => void;
}

const speciesOptions = [
  "Cachorro",
  "Gato",
  "Ave",
  "Roedor",
  "Reptil",
  "Outros",
];

const PetDetails = (props: PetDetailsProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [petData, setPetData] = useState<IPetPopulated | null>();
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [tutorApiData, setTutorApiData] = useState<any>(null);
  const [loadingTutorApi, setLoadingTutorApi] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(
    "(max-height: 768px) and (max-width: 1280px)"
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });
  // const attachmentUserToSend = useState<IPedido>({} as IPedido); // não utilizado
  // Mantém para compatibilidade - não remover
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [attachmentUser, setAttachmentUser] = useState<IPedido[]>([]);
  // Estados separados para filtros por categoria
  const [pedidosCompras, setPedidosCompras] = useState<IPedido[]>([]);
  const [pedidosAtendimentos, setPedidosAtendimentos] = useState<IPedido[]>([]);
  const [avatarSrc, setAvatarSrc] = React.useState<string | undefined>(
    undefined
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Estados para edição do tutor
  const [isEditingTutor, setIsEditingTutor] = useState(false);
  const [editedTutorData, setEditedTutorData] = useState<any>({});
  const [savingTutor, setSavingTutor] = useState(false);

  // Estado para controlar a expansão da gaveta
  const [expandedDrawer, setExpandedDrawer] = useState(false);

  // Estados para informações adicionais do pet
  const [ultimaVermifugacao, setUltimaVermifugacao] = useState("");
  const [marcaVermifugacao, setMarcaVermifugacao] = useState("");
  const [ultimoParasitario, setUltimoParasitario] = useState("");
  const [marcaParasitario, setMarcaParasitario] = useState("");
  const [ultimaVacinaMultipla, setUltimaVacinaMultipla] = useState("");
  const [ultimaVacinaPuppy, setUltimaVacinaPuppy] = useState("");
  const [ultimaVacinaGiardia, setUltimaVacinaGiardia] = useState("");
  const [ultimaVacinaRaiva, setUltimaVacinaRaiva] = useState("");
  const [ultimaVacinaGripe, setUltimaVacinaGripe] = useState("");
  const [ultimaAplicacao, setUltimaAplicacao] = useState("");
  const [tipoAplicacao, setTipoAplicacao] = useState("");
  const [primeiraRacao, setPrimeiraRacao] = useState("");
  const [quantidadeRacao, setQuantidadeRacao] = useState("");
  const [observacoes, setObservacoes] = useState("");

  // Estados para datas das próximas aplicações
  const [proximaVermifugacao, setProximaVermifugacao] = useState("");
  const [proximoParasitario, setProximoParasitario] = useState("");
  const [proximaAplicacao, setProximaAplicacao] = useState("");
  const [proximaVacinaMultipla, setProximaVacinaMultipla] = useState("");
  const [proximaVacinaPuppy, setProximaVacinaPuppy] = useState("");
  const [proximaVacinaGiardia, setProximaVacinaGiardia] = useState("");
  const [proximaVacinaRaiva, setProximaVacinaRaiva] = useState("");
  const [proximaVacinaGripe, setProximaVacinaGripe] = useState("");

  // Estados para armazenar os IDs das notificações associadas
  const [notificationIds, setNotificationIds] = useState<
    Record<string, string>
  >({});

  // Estados para gerenciamento de arquivos
  const [petFiles, setPetFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileDescription, setFileDescription] = useState("");

  // Estados para visualização de arquivos
  const [fileViewModal, setFileViewModal] = useState(false);
  const [selectedFileForView, setSelectedFileForView] = useState<any>(null);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>(
    {}
  );

  // State for modal
  const [modalAddAtendimento, setModalAddAtendimento] = useState(false);
  const [modalAddVenda, setModalAddVenda] = useState(false);
  const [modalDetalhesPedido, setModalDetalhesPedido] = useState(false);
  const [modalEditarPedido, setModalEditarPedido] = useState(false);
  const [pedidoSelecionado, setPedidoSelecionado] = useState<IPedido | null>(
    null
  );

  // Estados para modal de notificação
  const [notificationModal, setNotificationModal] = useState(false);
  const [notificationType, setNotificationType] = useState<string>("");
  const [notificationDate, setNotificationDate] = useState("");
  const [notificationTime, setNotificationTime] = useState("09:00");
  const [creatingNotification, setCreatingNotification] = useState(false);

  // Estado para modal de confirmação de exclusão
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deletingPet, setDeletingPet] = useState(false);

  // Estados para notificações do pet
  const [petNotifications, setPetNotifications] = useState<INotification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Função para abrir modal de detalhes
  const handleAbrirDetalhes = (pedido: IPedido) => {
    setPedidoSelecionado(pedido);
    setModalDetalhesPedido(true);
  };

  // Função para fechar modal de detalhes
  const handleFecharDetalhes = () => {
    setModalDetalhesPedido(false);
    setPedidoSelecionado(null);
  };

  // Função para abrir modal de edição
  const handleAbrirEdicao = (pedido: IPedido) => {
    setPedidoSelecionado(pedido);
    setModalEditarPedido(true);
  };

  // Função para fechar modal de edição
  const handleFecharEdicao = () => {
    setModalEditarPedido(false);
    setPedidoSelecionado(null);
  };

  // Função para abrir modal de notificação
  const handleOpenNotificationModal = (type: string) => {
    setNotificationType(type);
    // Define uma data padrão (30 dias a partir de hoje para vacinas/vermifugação)
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30);
    setNotificationDate(futureDate.toISOString().split("T")[0]);
    setNotificationModal(true);
  };

  // Função para fechar modal de notificação
  const handleCloseNotificationModal = () => {
    setNotificationModal(false);
    setNotificationType("");
    setNotificationDate("");
    setNotificationTime("09:00");
  };

  // Função para marcar notificação como concluída
  const handleMarkAsCompleted = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setSnackbar({
        open: true,
        message: "Notificação marcada como concluída!",
        severity: "success",
      });
      // Recarregar lista de notificações
      await reloadPetNotifications();
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao marcar notificação como concluída",
        severity: "error",
      });
    }
  };

  // Função para recarregar notificações do pet
  const reloadPetNotifications = async () => {
    const petId =
      (petData as any)?.id ||
      (petData as any)?._id ||
      (props.initialData as any)?.id ||
      (props.initialData as any)?._id;
    if (!petId) return;

    try {
      setLoadingNotifications(true);
      const today = new Date().toISOString().split("T")[0];

      // Calcular data limite (4 anos a partir de hoje para pegar todas as notificações futuras)
      const limitDate = new Date();
      limitDate.setDate(limitDate.getDate() + 1460);
      const endDate = limitDate.toISOString().split("T")[0];

      const response = await getNotifications({
        isActive: true,
        // isRead: false,
        startDate: today,
        endDate: endDate,
        limit: 10000000, // Garantir que pegue todas as notificações futuras
      });

      // Filtrar apenas notificações deste pet
      const petNotifs = response.data.filter((notif) => notif.petId === petId);

      // Ordenar por data agendada (mais próximas primeiro)
      const sortedNotifs = petNotifs.sort((a, b) => {
        const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime}`);
        const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime}`);
        return dateA.getTime() - dateB.getTime();
      });

      setPetNotifications(sortedNotifs);
    } catch (error) {
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Função para criar notificação
  const handleCreateNotification = async () => {
    if (!notificationDate || !petData) return;

    setCreatingNotification(true);

    try {
      const petId = (petData as any)?.id || (petData as any)?._id;

      if (!petId) {
        alert("Erro: Pet não identificado. Não é possível criar notificação.");
        setCreatingNotification(false);
        return;
      }

      const titles: Record<string, string> = {
        "Vacina Múltipla": "Vacina Múltipla",
        "Vacina Puppy": "Vacina Puppy",
        "Vacina Giardia": "Vacina Giardia",
        "Vacina Raiva": "Vacina de Raiva",
        "Vacina Gripe": "Vacina de Gripe",
        Vermifugação: "Vermifugação",
        Parasitário: "Parasitário",
        Aplicação: "Aplicação",
      };

      const summaries: Record<string, string> = {
        "Vacina Múltipla": `Aplicar próxima dose da vacina múltipla em ${petData.name}`,
        "Vacina Puppy": `Aplicar próxima dose da vacina puppy em ${petData.name}`,
        "Vacina Giardia": `Aplicar próxima dose da vacina de giardia em ${petData.name}`,
        "Vacina Raiva": `Aplicar próxima dose da vacina de raiva em ${petData.name}`,
        "Vacina Gripe": `Aplicar próxima dose da vacina de gripe em ${petData.name}`,
        Vermifugação: `Aplicar próxima dose de vermifugação em ${petData.name}`,
        Parasitário: `Aplicar próximo parasitário em ${petData.name}`,
        Aplicação: `Aplicar próxima dose/medicação em ${petData.name}`,
      };

      // Criar data com horário 12:00:00 para evitar problemas de timezone
      const dateWithNoon = new Date(notificationDate + "T12:00:00");

      const notificationPayload = {
        title: titles[notificationType] || notificationType,
        summary:
          summaries[notificationType] ||
          `Lembrete: ${notificationType} - ${petData.name}`,
        details: `Pet: ${petData.name}\nTutor: ${petData.tutor.name}\nRaça: ${petData.breed || "Não especificada"}`,
        scheduledDate: dateWithNoon.toISOString(),
        scheduledTime: notificationTime,
        petId: petId,
      };

      await createNotification(notificationPayload);

      // Recarregar lista de notificações
      await reloadPetNotifications();

      setSnackbar({
        open: true,
        message: `Notificação de ${notificationType} criada com sucesso!`,
        severity: "success",
      });
      handleCloseNotificationModal();
    } catch (error: any) {
      alert(
        `Erro ao criar notificação: ${error.response?.data?.details || error.message}`
      );
    } finally {
      setCreatingNotification(false);
    }
  };

  // Função para abrir modal de confirmação de exclusão
  const handleOpenDeleteModal = () => {
    setDeleteConfirmModal(true);
  };

  // Função para fechar modal de confirmação de exclusão
  const handleCloseDeleteModal = () => {
    setDeleteConfirmModal(false);
  };

  // Função para deletar o pet
  const handleDeletePet = async () => {
    if (!petData) return;

    setDeletingPet(true);
    try {
      const petId =
        (petData as any)?.id ||
        (petData as any)?._id ||
        (props.initialData as any)?.id ||
        props.initialData?._id;

      if (!petId) {
        setSnackbar({
          open: true,
          message: "Erro: ID do pet não encontrado.",
          severity: "error",
        });
        return;
      }

      await deletePet(petId);

      setSnackbar({
        open: true,
        message: `${petData.name} foi excluído com sucesso!`,
        severity: "success",
      });

      handleCloseDeleteModal();

      // Aguarda um pouco para o usuário ver a mensagem antes de fechar
      setTimeout(() => {
        if (props.onSave) props.onSave();
        if (props.onClose) props.onClose();
      }, 1500);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Erro ao excluir pet: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setDeletingPet(false);
    }
  };

  // Função para iniciar a edição do tutor
  const handleStartEditTutor = () => {
    const tutor = props.initialData?.tutor;

    setEditedTutorData({
      name:
        tutorApiData?.NomeFantasia ||
        tutorApiData?.RazaoSocial ||
        tutor?.name ||
        "",
      cpf: tutorApiData?.CNPJ_CPF || tutor?.cpf || "",
      email: tutorApiData?.Email || tutor?.email || "",
      phone:
        tutorApiData?.Telefone ||
        (tutor as any)?.phone ||
        (tutor as any)?.telefone ||
        "",
      mobile:
        tutorApiData?.Celular ||
        (tutor as any)?.mobile ||
        (tutor as any)?.celular ||
        "",
      secondaryPhone:
        (tutor as any)?.secondaryPhone ||
        (tutor as any)?.telefonesSecundarios?.[0] ||
        "",
      addressStreet:
        tutorApiData?.EnderecoPadrao?.Logradouro ||
        tutorApiData?.Logradouro ||
        (tutor as any)?.addressStreet ||
        (tutor as any)?.logradouro ||
        "",
      addressNumber:
        tutorApiData?.EnderecoPadrao?.Numero ||
        tutorApiData?.LogradouroNumero ||
        (tutor as any)?.addressNumber ||
        (tutor as any)?.logradouroNumero ||
        "",
      addressDistrict:
        tutorApiData?.EnderecoPadrao?.Bairro ||
        tutorApiData?.Bairro ||
        (tutor as any)?.addressDistrict ||
        (tutor as any)?.bairro ||
        "",
      addressCity:
        tutorApiData?.EnderecoPadrao?.Cidade ||
        tutorApiData?.Cidade ||
        (tutor as any)?.addressCity ||
        (tutor as any)?.cidade ||
        "",
      addressZip:
        tutorApiData?.EnderecoPadrao?.CEP ||
        tutorApiData?.CEP ||
        (tutor as any)?.addressZip ||
        (tutor as any)?.cep ||
        "",
      addressState:
        tutorApiData?.EnderecoPadrao?.Uf ||
        tutorApiData?.Uf ||
        (tutor as any)?.addressState ||
        (tutor as any)?.uf ||
        "",
      birthDate: (() => {
        const dateValue = tutorApiData?.DataNascimento || tutor?.birthDate;
        if (!dateValue) return "";

        const date = new Date(dateValue);
        // Verificar se a data é válida
        if (isNaN(date.getTime())) return "";

        return date.toISOString().split("T")[0];
      })(),
    });
    setIsEditingTutor(true);
  };

  // Função para cancelar a edição do tutor
  const handleCancelEditTutor = () => {
    setIsEditingTutor(false);
    setEditedTutorData({});
  };

  // Função para atualizar campo do tutor editado
  const handleTutorFieldChange = (field: string, value: string) => {
    let formattedValue = value;

    // Aplica formatação específica dependendo do campo
    if (field === "cpf") {
      formattedValue = formatCpfCnpj(value);
    } else if (
      field === "phone" ||
      field === "mobile" ||
      field === "secondaryPhone"
    ) {
      formattedValue = formatPhone(value);
    }

    setEditedTutorData((prev: any) => ({
      ...prev,
      [field]: formattedValue,
    }));
  };

  // Função para salvar as alterações do tutor
  const handleSaveTutor = async () => {
    if (!props.initialData?.tutor) {
      setSnackbar({
        open: true,
        message: "Tutor não encontrado",
        severity: "error",
      });
      return;
    }

    // Validação: nome é obrigatório
    if (!editedTutorData.name || !editedTutorData.name.trim()) {
      setSnackbar({
        open: true,
        message: "O nome do tutor é obrigatório",
        severity: "error",
      });
      return;
    }

    // Validação: pelo menos um telefone é obrigatório
    if (
      (!editedTutorData.phone || !editedTutorData.phone.trim()) &&
      (!editedTutorData.mobile || !editedTutorData.mobile.trim())
    ) {
      setSnackbar({
        open: true,
        message: "Pelo menos um telefone (fixo ou celular) é obrigatório",
        severity: "error",
      });
      return;
    }

    setSavingTutor(true);
    try {
      const tutorId =
        (props.initialData.tutor as any)?.id ||
        (props.initialData.tutor as any)?._id;

      if (!tutorId) {
        throw new Error("ID do tutor não encontrado");
      }

      // Preparar dados para envio - converter strings vazias em null e remover formatação
      const dataToSend = {
        name: editedTutorData.name?.trim() || null,
        cpf: editedTutorData.cpf ? unformatCpfCnpj(editedTutorData.cpf) : null,
        email: editedTutorData.email?.trim() || null,
        phone: editedTutorData.phone
          ? formatPhone(editedTutorData.phone)
          : null,
        mobile: editedTutorData.mobile
          ? formatPhone(editedTutorData.mobile)
          : null,
        secondaryPhone: editedTutorData.secondaryPhone
          ? formatPhone(editedTutorData.secondaryPhone)
          : null,
        addressStreet: editedTutorData.addressStreet?.trim() || null,
        addressNumber: editedTutorData.addressNumber?.trim() || null,
        addressDistrict: editedTutorData.addressDistrict?.trim() || null,
        addressCity: editedTutorData.addressCity?.trim() || null,
        addressZip: editedTutorData.addressZip?.trim() || null,
        addressState: editedTutorData.addressState?.trim() || null,
        birthDate: editedTutorData.birthDate?.trim()
          ? `${editedTutorData.birthDate}T12:00:00`
          : null,
      };

      // Atualizar tutor (sincroniza com API externa automaticamente)
      await updateTutor(tutorId, dataToSend);

      setSnackbar({
        open: true,
        message: "Tutor atualizado com sucesso! Sincronizando com ERP...",
        severity: "success",
      });

      // Atualizar o estado local do petData com os novos dados do tutor
      setPetData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tutor: {
            ...(prev.tutor || {}),
            name: dataToSend.name || prev.tutor?.name || "",
            cpf: dataToSend.cpf || prev.tutor?.cpf || "",
            email: dataToSend.email || prev.tutor?.email,
            phone: dataToSend.phone || prev.tutor?.phone,
            mobile: dataToSend.mobile || prev.tutor?.mobile,
            secondaryPhone:
              dataToSend.secondaryPhone || prev.tutor?.secondaryPhone,
            id: tutorId,
            birthDate: prev.tutor?.birthDate || new Date(),
            createdAt: prev.tutor?.createdAt || new Date(),
          },
        };
      });

      setIsEditingTutor(false);
      setEditedTutorData({});

      // Aguardar um pouco para dar tempo da API externa processar
      setTimeout(() => {
        fetchTutorApiData();
      }, 1500);

      // Atualizar a lista de pets se necessário
      if (props.onSave) {
        props.onSave();
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Erro ao atualizar tutor: ${error.response?.data?.details || error.message}`,
        severity: "error",
      });
    } finally {
      setSavingTutor(false);
    }
  };

  // Função para recarregar pedidos do tutor
  const refetchPedidos = async () => {
    setLoadingPedidos(true);
    try {
      const tutorCpf = petData?.tutor.cpf;
      const tutorName = petData?.tutor.name;

      // Se não houver CPF, tentar buscar apenas pelo nome
      const searchParams: any = {
        cliente: tutorName,
      };

      // Só adiciona CPF se ele existir e não for null/undefined/vazio
      if (tutorCpf && tutorCpf.trim() !== "") {
        searchParams.cpfcnpj = tutorCpf;
      }

      const pedidosData = await getPedidosByUser(searchParams);

      if (pedidosData) {
        // Ordenar todos os pedidos do cliente por data (mais recentes primeiro)
        const todosPedidosOrdenados = pedidosData.sort(sortPedidosDesc);

        // Filtrar todos os pedidos por categoria
        // Histórico de Compras: apenas "Venda de Produtos"
        const todosCompras = todosPedidosOrdenados.filter(
          (pedido: IPedido) => pedido.Categoria === "Venda de Produtos"
        );

        // Histórico de Atendimentos: apenas "Venda de Serviços"
        const todosAtendimentos = todosPedidosOrdenados.filter(
          (pedido: IPedido) => pedido.Categoria === "Venda de Serviços"
        );

        // Atualizar estados com todos os pedidos do cliente
        setAttachmentUser(todosPedidosOrdenados);
        setPedidosCompras(todosCompras);
        setPedidosAtendimentos(todosAtendimentos);
      }
    } catch (error) {
    } finally {
      setLoadingPedidos(false);
    }
  };

  useEffect(() => {
    setLoadingPedidos(true);
    let isMounted = true;

    const fetchData = async () => {
      try {
        const tutorCpf = props.initialData?.tutor.cpf;
        const tutorName = props.initialData?.tutor.name;

        // Se não houver CPF, tentar buscar apenas pelo nome
        const searchParams: any = {
          cliente: tutorName,
        };

        // Só adiciona CPF se ele existir e não for null/undefined/vazio
        if (tutorCpf && tutorCpf.trim() !== "") {
          searchParams.cpfcnpj = tutorCpf;
        }

        const pedidosData = await getPedidosByUser(searchParams);

        if (isMounted && pedidosData) {
          // Ordenar todos os pedidos do cliente por data (mais recentes primeiro)
          const todosPedidosOrdenados = pedidosData.sort(sortPedidosDesc);

          // Filtrar todos os pedidos por categoria
          // Histórico de Compras: apenas "Venda de Produtos"
          const todosCompras = todosPedidosOrdenados.filter(
            (pedido: IPedido) => pedido.Categoria === "Venda de Produtos"
          );

          // Histórico de Atendimentos: apenas "Venda de Serviços"
          const todosAtendimentos = todosPedidosOrdenados.filter(
            (pedido: IPedido) => pedido.Categoria === "Venda de Serviços"
          );

          // Atualizar estados com todos os pedidos do cliente
          setAttachmentUser(todosPedidosOrdenados);
          setPedidosCompras(todosCompras);
          setPedidosAtendimentos(todosAtendimentos);
        }
      } catch (error) {
        // Erro silencioso
      } finally {
        if (isMounted) {
          setLoadingPedidos(false);
        }
      }
    };

    if (props.initialData) {
      const initialPet = props.initialData as IPetPopulated & {
        deceased?: boolean;
      };

      // O backend usa `deceased`, enquanto este formulário trabalha com
      // `isDeceased`. Manter os dois formatos evita exibir o switch desligado
      // após a atualização e recarga dos dados.
      setPetData({
        ...initialPet,
        isDeceased: initialPet.isDeceased ?? initialPet.deceased ?? false,
      });

      // Carregar histórico do pet da API
      const loadPetHistory = async () => {
        try {
          const petId =
            (props.initialData as any)?.id || (props.initialData as any)?._id;
          if (petId) {
            const latestHistory = await getLatestPetHistory(petId);
            if (latestHistory) {
              // Converter datas para formato YYYY-MM-DD para os inputs de data
              if (latestHistory.ultimaVermifugacao) {
                setUltimaVermifugacao(
                  new Date(latestHistory.ultimaVermifugacao)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.marcaVermifugacao)
                setMarcaVermifugacao(latestHistory.marcaVermifugacao);
              if (latestHistory.ultimoParasitario) {
                setUltimoParasitario(
                  new Date(latestHistory.ultimoParasitario)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.marcaParasitario)
                setMarcaParasitario(latestHistory.marcaParasitario);
              if (latestHistory.ultimaVacinaMultipla) {
                setUltimaVacinaMultipla(
                  new Date(latestHistory.ultimaVacinaMultipla)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.ultimaVacinaPuppy) {
                setUltimaVacinaPuppy(
                  new Date(latestHistory.ultimaVacinaPuppy)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.ultimaVacinaGiardia) {
                setUltimaVacinaGiardia(
                  new Date(latestHistory.ultimaVacinaGiardia)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.ultimaVacinaRaiva) {
                setUltimaVacinaRaiva(
                  new Date(latestHistory.ultimaVacinaRaiva)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.ultimaVacinaGripe) {
                setUltimaVacinaGripe(
                  new Date(latestHistory.ultimaVacinaGripe)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.ultimaAplicacao) {
                setUltimaAplicacao(
                  new Date(latestHistory.ultimaAplicacao)
                    .toISOString()
                    .split("T")[0]
                );
              }
              if (latestHistory.tipoAplicacao)
                setTipoAplicacao(latestHistory.tipoAplicacao);
              if (latestHistory.primeiraRacao)
                setPrimeiraRacao(latestHistory.primeiraRacao);
              if (latestHistory.quantidadeRacao)
                setQuantidadeRacao(latestHistory.quantidadeRacao);
              if (latestHistory.observacoes)
                setObservacoes(latestHistory.observacoes);
            }
          }
        } catch (error) {}
      };

      loadPetHistory();
    }

    // Carregar notificações futuras do pet
    const loadPetNotifications = async () => {
      const petId =
        (props.initialData as any)?.id || (props.initialData as any)?._id;
      if (!petId) return;

      try {
        setLoadingNotifications(true);
        const today = new Date().toISOString().split("T")[0];

        // Calcular data limite (4 anos a partir de hoje para pegar todas as notificações futuras)
        const limitDate = new Date();
        limitDate.setDate(limitDate.getDate() + 1460);
        const endDate = limitDate.toISOString().split("T")[0];

        const response = await getNotifications({
          isActive: true,
          // isRead: false
          startDate: today,
          endDate: endDate,
          limit: 10000000,
        });

        // Filtrar apenas notificações deste pet
        const petNotifs = response.data.filter(
          (notif) => notif.petId === petId
        );

        // Ordenar por data agendada (mais próximas primeiro)
        const sortedNotifs = petNotifs.sort((a, b) => {
          const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime}`);
          const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime}`);
          return dateA.getTime() - dateB.getTime();
        });

        setPetNotifications(sortedNotifs);
      } catch (error) {
      } finally {
        setLoadingNotifications(false);
      }
    };

    if (props.initialData) {
      loadPetNotifications();
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [props.initialData]);

  // Função para atualizar as próximas datas baseadas nas notificações existentes
  const updateNextDatesFromNotifications = useCallback(() => {
    // Mapear títulos de notificações para os setters correspondentes
    const notificationMapping: Record<string, (date: string) => void> = {
      "Vacina Múltipla": setProximaVacinaMultipla,
      "Vacina Puppy": setProximaVacinaPuppy,
      "Vacina Giardia": setProximaVacinaGiardia,
      "Vacina de Raiva": setProximaVacinaRaiva,
      "Vacina de Gripe": setProximaVacinaGripe,
      Vermifugação: setProximaVermifugacao,
      Parasitário: setProximoParasitario,
      Aplicação: setProximaAplicacao,
    };

    if (!petNotifications || petNotifications.length === 0) {
      Object.values(notificationMapping).forEach((setter) => setter(""));
      setNotificationIds({});
      return;
    }

    const newNotificationIds: Record<string, string> = {};

    // Para cada tipo de notificação, pegar a mais próxima (primeira não lida)
    Object.entries(notificationMapping).forEach(([title, setter]) => {
      // Filtrar todas as notificações deste tipo que não foram lidas
      const typeNotifications = petNotifications.filter(
        (notif) => notif.title === title && !notif.isRead
      );

      // Pegar a mais próxima (como já estão ordenadas, é a primeira)
      const matchingNotification =
        typeNotifications.length > 0 ? typeNotifications[0] : undefined;

      if (matchingNotification) {
        try {
          const date = new Date(matchingNotification.scheduledDate);
          if (!isNaN(date.getTime())) {
            const formattedDate = date.toISOString().split("T")[0];
            setter(formattedDate);
            newNotificationIds[title] = matchingNotification.id;
          }
        } catch (error) {}
      } else {
        setter("");
      }
    });
    setNotificationIds(newNotificationIds);
  }, [petNotifications]);

  // useEffect para atualizar as próximas datas quando as notificações mudarem
  useEffect(() => {
    updateNextDatesFromNotifications();
  }, [petNotifications, updateNextDatesFromNotifications]);

  // Função para buscar dados do tutor na API externa com múltiplos critérios
  const fetchTutorApiData = async () => {
    const tutor = props.initialData?.tutor;
    if (!tutor) {
      setTutorApiData(null);
      return;
    }

    // Extrair critérios de busca disponíveis
    const cpfCnpj = tutor.cpf;
    const nomeFantasia = tutor.name;
    // Pegar erpId do tutor (ID da API Externa, não o UUID do banco local)
    const erpId = (tutor as any)?.erpId || (tutor as any)?.idERP;

    // Se não houver nenhum critério, não buscar
    if (!cpfCnpj && !nomeFantasia) {
      setTutorApiData(null);
      return;
    }

    setLoadingTutorApi(true);
    try {
      const API_URL =
        process.env.REACT_APP_API_FP_BE || "http://localhost:5000/api";

      // Construir query params
      // Se houver CPF, busca primeiro por CPF (mais específico)
      // Se não houver CPF, busca por nome
      const params = new URLSearchParams();
      if (cpfCnpj) {
        params.append("cpfcnpj", cpfCnpj);
      } else if (nomeFantasia) {
        params.append("nomefantasia", nomeFantasia);
      }

      const url = `${API_URL}/external/pessoas/search?${params.toString()}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.length > 0) {
        let selectedPerson = data[0]; // Padrão: primeiro resultado

        // Se houver múltiplos resultados e temos erpId, filtrar pelo ID
        if (data.length > 1 && erpId) {
          const matchedPerson = data.find((person: any) => person.ID === erpId);

          if (matchedPerson) {
            selectedPerson = matchedPerson;
          }
        }

        setTutorApiData(selectedPerson);
      } else {
        setTutorApiData(null);
      }
    } catch (error) {
      setTutorApiData(null);
    } finally {
      setLoadingTutorApi(false);
    }
  };

  // useEffect para buscar dados do tutor quando os dados mudarem
  useEffect(() => {
    const tutor = props.initialData?.tutor;

    // Pegar erpId (ID da API Externa, não o UUID do banco local)
    const erpId = (tutor as any)?.erpId || (tutor as any)?.idERP;
    const cpfCnpj = tutor?.cpf;

    // PostgreSQL usa 'phone' e 'mobile', MongoDB antigo usava 'telefone' e 'celular'
    const telefone =
      (tutor as any)?.phone ||
      (tutor as any)?.mobile ||
      (tutor as any)?.telefone ||
      (tutor as any)?.celular;

    if (erpId || cpfCnpj || telefone) {
      fetchTutorApiData();
    } else {
      setTutorApiData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialData?.tutor?.cpf, props.initialData?.tutor?.name]);

  const handleChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setPetData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: value ?? "",
        name: name === "name" ? (value ?? "") : (prev.name ?? ""),
        gender: name === "gender" ? (value ?? "") : (prev.gender ?? ""),
        breed: name === "breed" ? (value ?? "") : (prev.breed ?? ""),
        // age não deve ser editado manualmente
        weight: name === "weight" ? (value ?? "") : (prev.weight ?? ""),
        species: name === "species" ? (value ?? "") : (prev.species ?? ""),
        isDeceased:
          name === "isDeceased" ? (value ?? false) : (prev.isDeceased ?? false),
        deathDate:
          name === "deathDate" ? (value ?? "") : (prev.deathDate ?? ""),
        dateOfBirth:
          name === "dateOfBirth" ? (value ?? "") : (prev.dateOfBirth ?? ""),
        dateOfPurchase:
          name === "dateOfPurchase"
            ? (value ?? "")
            : (prev.dateOfPurchase ?? ""),
        color: name === "color" ? (value ?? "") : (prev.color ?? ""),
      };
    });
  };

  const handleSwitchChange = (e: { target: { name: any; checked: any } }) => {
    const { name, checked } = e.target;
    setPetData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [name]: checked,
        name: prev.name,
        gender: prev.gender,
        breed: prev.breed,
        // manter age intacto
        foto: prev.foto,
        weight: prev.weight,
        species: prev.species,
        isDeceased:
          name === "isDeceased" ? checked : (prev.isDeceased ?? false),
        deathDate: prev.deathDate,
        dateOfBirth: prev.dateOfBirth,
        color: prev.color,
      };
    });
  };

  const handleSubmit = async () => {
    let imageUrl = "";
    if (!petData) {
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Garantir que temos um tutor válido
      if (!petData.tutor) {
        throw new Error("Tutor do pet não está definido");
      }

      if (selectedFile) {
        // Você precisa ter o userId disponível aqui
        imageUrl = await uploadImage(selectedFile);
      }
      const petId =
        (petData as any)?.id ||
        (petData as any)?._id ||
        (props.initialData as any)?.id ||
        props.initialData?._id;
      if (!petId) {
        setError("ID do pet não encontrado para atualização.");
        setIsSubmitting(false);
        return;
      }

      const dataToSend = normalizePetUpdatePayload(petData, {
        foto: selectedFile ? imageUrl : undefined,
      });

      console.debug("[Pet Update] id=", petId, "payload=", dataToSend);
      const updatedPet = await updatePet(petId, dataToSend as any);
      const persistedDeceased =
        (updatedPet as any)?.deceased ?? (updatedPet as any)?.isDeceased;

      if (persistedDeceased !== Boolean(petData.isDeceased)) {
        throw new Error("O status de óbito não foi confirmado pelo servidor.");
      }

      // Salvar informações de histórico separadamente
      const historyData: IPetHistoryData = {
        ultimaVermifugacao: ultimaVermifugacao || undefined,
        marcaVermifugacao: marcaVermifugacao || undefined,
        ultimoParasitario: ultimoParasitario || undefined,
        marcaParasitario: marcaParasitario || undefined,
        ultimaAplicacao: ultimaAplicacao || undefined,
        tipoAplicacao: tipoAplicacao || undefined,
        ultimaVacinaMultipla: ultimaVacinaMultipla || undefined,
        ultimaVacinaPuppy: ultimaVacinaPuppy || undefined,
        ultimaVacinaGiardia: ultimaVacinaGiardia || undefined,
        ultimaVacinaRaiva: ultimaVacinaRaiva || undefined,
        ultimaVacinaGripe: ultimaVacinaGripe || undefined,
        primeiraRacao: primeiraRacao || undefined,
        quantidadeRacao: quantidadeRacao || undefined,
        observacoes: observacoes || undefined,
      };

      // Verificar se há pelo menos alguma informação para salvar
      const hasHistoryData = Object.values(historyData).some(
        (value) => value !== undefined && value !== ""
      );

      if (hasHistoryData) {
        console.debug(
          "[History Update] petId=",
          petId,
          "historyData=",
          historyData
        );
        await createOrUpdatePetHistory(petId, historyData);
      }

      if (props.onSave) props.onSave();
      if (props.onClose) props.onClose();
    } catch (error) {
      setError("Erro ao atualizar pet. Por favor, tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Funções para gerenciamento de arquivos dos pets
  const loadPetFiles = useCallback(async () => {
    if (!petData?.id) return;

    setLoadingFiles(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_FP_BE}/pets/${petData.id}/files`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const files = await response.json();
        setPetFiles(files);

        // Carregar URLs assinadas para thumbnails de imagens e PDFs
        const thumbnails: Record<string, string> = {};
        for (const file of files) {
          if (file.fileType.startsWith("image/") || isPdfFile(file.fileType)) {
            const signedUrl = await getSignedImageUrl(file.id);
            if (signedUrl) {
              thumbnails[file.id] = signedUrl;
            }
          }
        }
        setThumbnailUrls(thumbnails);
      } else {
      }
    } catch (error) {
    } finally {
      setLoadingFiles(false);
    }
  }, [petData?.id]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    //
    //

    if (!file || !petData?.id) {
      //
      return;
    }

    //
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("description", fileDescription);

      const token = localStorage.getItem("token");
      const url = `${process.env.REACT_APP_API_FP_BE}/pets/${petData.id}/files`;
      //
      //
      //
      //

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      //

      if (response.ok) {
        //
        setFileDescription("");
        await loadPetFiles(); // Recarregar lista de arquivos
        // Resetar o input
        event.target.value = "";
      } else {
        const error = await response.json();
      }
    } catch (error) {
    } finally {
      setUploadingFile(false);
    }
  };

  // Função para buscar URL assinada para visualização de imagem
  const getSignedImageUrl = async (fileId: string): Promise<string | null> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_FP_BE}/pet-files/${fileId}/download`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.downloadUrl;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  const handleFileDownload = async (fileId: string, fileName: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_FP_BE}/pet-files/${fileId}/download`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Criar elemento <a> temporário para forçar download
        const link = document.createElement("a");
        link.href = data.downloadUrl;
        link.download = fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
      }
    } catch (error) {}
  };

  const handleFileDelete = async (fileId: string) => {
    if (!window.confirm("Tem certeza que deseja excluir este arquivo?")) return;

    try {
      const token = localStorage.getItem("token");
      const url = `${process.env.REACT_APP_API_FP_BE}/pet-files/${fileId}`;

      //
      //
      //
      //

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      //

      if (response.ok) {
        //
        await loadPetFiles(); // Recarregar lista de arquivos
      } else {
        const errorData = await response.json();
      }
    } catch (error) {}
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) return <Image />;
    if (fileType === "application/pdf") return <PictureAsPdf />;
    return <AttachFile />;
  };

  const handleViewFile = async (file: any) => {
    //

    // Se for uma imagem ou PDF, abrir modal de visualização
    if (file.fileType.startsWith("image/") || isPdfFile(file.fileType)) {
      // Buscar URL assinada para o arquivo
      const signedUrl = await getSignedImageUrl(file.id);
      if (signedUrl) {
        setSelectedFileForView({ ...file, signedUrl });
        setFileViewModal(true);
      } else {
      }
    } else {
      // Para outros tipos de arquivo, fazer download
      handleFileDownload(file.id, file.originalName);
    }
  };

  const isImageFile = (fileType: string) => {
    return fileType.startsWith("image/");
  };

  const isPdfFile = (fileType: string) => {
    return fileType === "application/pdf";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // useEffect para carregar arquivos quando a tab de arquivos for acessada ou pet mudar
  useEffect(() => {
    const isFilesTab =
      (!props.tecnico?.Tecnico && activeTab === 3) ||
      (props.tecnico?.Tecnico && activeTab === 4);
    if (petData?.id && isFilesTab) {
      loadPetFiles();
    }
  }, [petData?.id, activeTab, props.tecnico?.Tecnico, loadPetFiles]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: { xs: 1, sm: isSmallScreen ? 1 : 2 },
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        {props.onClose && (
          <IconButton
            onClick={props.onClose}
            size={isMobile || isSmallScreen ? "small" : "medium"}
          >
            <Close />
          </IconButton>
        )}
        <Typography
          variant={isMobile || isSmallScreen ? "body1" : "h6"}
          component="h2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: isMobile || isSmallScreen ? "nowrap" : "normal",
            fontSize: { xs: "0.9rem", sm: isSmallScreen ? "1rem" : "1.25rem" },
          }}
        >
          {petData?.name +
            " - " +
            `${petData?.gender === "Femea" ? "Fêmea" : "Macho"}` +
            " - " +
            petData?.breed ||
            "" ||
            "Novo Pet"}
        </Typography>
      </Box>
      {error && (
        <Box
          sx={{
            px: 2,
            py: 1,
            backgroundColor: (t) => t.palette.error.light,
            color: (t) => t.palette.error.contrastText,
          }}
        >
          <Typography variant="caption">{error}</Typography>
        </Box>
      )}

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant={isMobile || isSmallScreen ? "scrollable" : "standard"}
        scrollButtons={isMobile || isSmallScreen ? "auto" : false}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: isMobile || isSmallScreen ? 40 : 48,
          "& .MuiTab-root": {
            minHeight: isMobile || isSmallScreen ? 40 : 48,
            fontSize: isMobile || isSmallScreen ? "0.7rem" : "0.875rem",
            px: isMobile || isSmallScreen ? 0.5 : 2,
          },
        }}
      >
        <Tab label="Cadastro" />
        {props.tecnico?.Tecnico && <Tab label="Tutor" />}
        <Tab
          label={isMobile || isSmallScreen ? "Compras" : "Histórico de Compras"}
        />
        {props.tecnico?.Tecnico && (
          <Tab
            label={
              isMobile || isSmallScreen
                ? "Atendimentos"
                : "Histórico de Atendimentos"
            }
          />
        )}
        <Tab label="Arquivos" />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: isMobile || isSmallScreen ? 1 : 2,
          pb: isMobile || isSmallScreen ? 2 : 4,
          minHeight: 0,
          maxHeight: isSmallScreen
            ? "calc(100vh - 280px)"
            : isMobile
              ? "calc(100vh - 350px)"
              : "calc(100vh - 320px)",
        }}
      >
        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: "center" }}>
              <ButtonBase
                component="label"
                role={undefined}
                tabIndex={-1}
                aria-label="Avatar image"
                sx={{
                  borderRadius: "40px",
                  "&:has(:focus-visible)": {
                    outline: "2px solid",
                    outlineOffset: "2px",
                  },
                }}
              >
                <Avatar
                  alt="Pet avatar"
                  src={avatarSrc || petData?.photo || petData?.foto}
                  sx={{ width: 100, height: 100 }}
                >
                  {!avatarSrc && !petData?.photo && !petData?.foto && <Pets />}
                </Avatar>
                <input
                  type="file"
                  accept="image/*"
                  style={{
                    border: 0,
                    clip: "rect(0 0 0 0)",
                    height: "1px",
                    margin: "-1px",
                    overflow: "hidden",
                    padding: 0,
                    position: "absolute",
                    whiteSpace: "nowrap",
                    width: "1px",
                  }}
                  onChange={handleAvatarChange}
                />
              </ButtonBase>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nome do Pet"
                name="name"
                value={petData?.name || ""}
                onChange={handleChange}
                margin="normal"
                required
                size={isSmallScreen ? "small" : "medium"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl
                fullWidth
                margin="normal"
                required
                size={isSmallScreen ? "small" : "medium"}
              >
                <InputLabel>Sexo</InputLabel>
                <Select
                  name="gender"
                  value={petData?.gender || ""}
                  onChange={handleChange}
                  label="Sexo"
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  <MenuItem value="Femea">Fêmea</MenuItem>
                  <MenuItem value="Macho">Macho</MenuItem>
                  <MenuItem value="Indeterminado">Indeterminado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Raça"
                name="breed"
                value={petData?.breed || ""}
                onChange={handleChange}
                margin="normal"
                required
                size={isSmallScreen ? "small" : "medium"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Idade"
                name="age"
                value={formatAge(petData?.age as any)}
                margin="normal"
                disabled
                size={isSmallScreen ? "small" : "medium"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Peso"
                name="weight"
                type="number"
                value={petData?.weight || ""}
                onChange={handleChange}
                margin="normal"
                size={isSmallScreen ? "small" : "medium"}
                InputProps={{
                  endAdornment: "Kg",
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl
                fullWidth
                margin="normal"
                required
                size={isSmallScreen ? "small" : "medium"}
              >
                <InputLabel>Espécie</InputLabel>
                <Select
                  name="species"
                  value={petData?.species || ""}
                  onChange={handleChange}
                  label="Espécie"
                  required
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {speciesOptions.map((species) => (
                    <MenuItem key={species} value={species}>
                      {species}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Cor"
                name="color"
                value={petData?.color || ""}
                onChange={handleChange}
                margin="normal"
                disabled={isSubmitting}
                size={isSmallScreen ? "small" : "medium"}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                name="dateOfBirth"
                type="date"
                size={isSmallScreen ? "small" : "medium"}
                value={
                  petData?.dateOfBirth
                    ? new Date(petData.dateOfBirth).toISOString().split("T")[0]
                    : ""
                }
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Data da compra"
                name="dateOfPurchase"
                type="date"
                size={isSmallScreen ? "small" : "medium"}
                value={
                  petData?.dateOfPurchase
                    ? new Date(petData.dateOfPurchase)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={petData?.isDeceased || false}
                    onChange={handleSwitchChange}
                    name="isDeceased"
                    color="primary"
                  />
                }
                label="Animal morto"
              />
              {petData?.isDeceased && (
                <TextField
                  fullWidth
                  label="Data da morte"
                  name="deathDate"
                  type="date"
                  value={petData?.deathDate || ""}
                  onChange={handleChange}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                  size={isSmallScreen ? "small" : "medium"}
                />
              )}
            </Grid>

            {/* Campo de Observações */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Observações"
                name="observacoes"
                multiline
                rows={isSmallScreen ? 2 : 3}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                margin="normal"
                placeholder="Adicione observações sobre o pet..."
                size={isSmallScreen ? "small" : "medium"}
              />
            </Grid>

            {/* Gaveta Expansível com Informações Detalhadas */}
            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Accordion
                expanded={expandedDrawer}
                onChange={() => setExpandedDrawer(!expandedDrawer)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 1,
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" color="primary">
                    Informações Veterinárias & Alimentação
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* Seção de Próximas Aplicações/Vacinas */}
                    {petNotifications.length > 0 && (
                      <>
                        <Grid size={{ xs: 12 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              mb: 2,
                              fontWeight: "bold",
                              color: "primary.main",
                            }}
                          >
                            Próximas Aplicações Agendadas (próximos 4 anos)
                          </Typography>
                        </Grid>

                        {petNotifications.map((notification) => {
                          // Validar e parsear a data
                          let scheduledDateTime: Date = new Date();
                          let isValidDate = false;

                          try {
                            // Tentar parsear a data do scheduledDate (que já vem como ISO string)
                            scheduledDateTime = new Date(
                              notification.scheduledDate
                            );

                            // Verificar se a data é válida
                            if (!isNaN(scheduledDateTime.getTime())) {
                              isValidDate = true;
                            }
                          } catch (error) {}

                          // Se a data for inválida, pular esta notificação
                          if (!isValidDate) {
                            return null;
                          }

                          const formattedDate = format(
                            scheduledDateTime,
                            "dd/MM/yyyy"
                          );
                          const formattedTime =
                            notification.scheduledTime || "09:00";

                          // Calcular dias restantes
                          const today = new Date();
                          today.setHours(0, 0, 0, 0); // Zerar horas para comparação correta
                          scheduledDateTime.setHours(0, 0, 0, 0);

                          const diffTime =
                            scheduledDateTime.getTime() - today.getTime();
                          const diffDays = Math.ceil(
                            diffTime / (1000 * 60 * 60 * 24)
                          );

                          // Verificar se é para hoje
                          const isToday = diffDays === 0;

                          return (
                            <Grid
                              size={{ xs: 12, sm: 6, md: 4 }}
                              key={notification.id}
                            >
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  borderLeft: 4,
                                  borderColor:
                                    diffDays <= 7
                                      ? "error.main"
                                      : "primary.main",
                                  "&:hover": {
                                    boxShadow: 4,
                                  },
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  {notification.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                >
                                  {notification.summary}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <Typography variant="caption" color="primary">
                                    {formattedDate} às {formattedTime}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    label={
                                      diffDays === 0
                                        ? "Hoje!"
                                        : diffDays === 1
                                          ? "Amanhã"
                                          : `Em ${diffDays} dias`
                                    }
                                    color={diffDays <= 7 ? "error" : "primary"}
                                    variant={
                                      diffDays <= 7 ? "filled" : "outlined"
                                    }
                                  />
                                  {notification.isRead ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Notificação já lida
                                    </Typography>
                                  ) : (
                                    isToday && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<CheckCircle />}
                                        onClick={() =>
                                          handleMarkAsCompleted(notification.id)
                                        }
                                      >
                                        Concluído
                                      </Button>
                                    )
                                  )}
                                </Box>
                              </Paper>
                            </Grid>
                          );
                        })}

                        <Grid size={{ xs: 12 }}>
                          <Divider sx={{ my: 2 }} />
                        </Grid>
                      </>
                    )}

                    {/* Seção de Vermifugação e Parasitários */}
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Vermifugação e Parasitários
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Vermifugação"
                          type="date"
                          value={ultimaVermifugacao}
                          onChange={(e) =>
                            setUltimaVermifugacao(e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Vermifugação")
                          }
                          title="Criar lembrete para próxima vermifugação"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Vermifugação"
                        type="date"
                        value={proximaVermifugacao}
                        onChange={(e) => setProximaVermifugacao(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Vermifugação"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Marca da Vermifugação"
                        value={marcaVermifugacao}
                        size={isSmallScreen ? "small" : "medium"}
                        onChange={(e) => setMarcaVermifugacao(e.target.value)}
                        placeholder="Ex: Drontal, Endal..."
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Último Parasitário"
                          type="date"
                          value={ultimoParasitario}
                          onChange={(e) => setUltimoParasitario(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Parasitário")
                          }
                          title="Criar lembrete para próximo parasitário"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próximo Parasitário"
                        type="date"
                        value={proximoParasitario}
                        onChange={(e) => setProximoParasitario(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Parasitário"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Marca do Parasitário"
                        value={marcaParasitario}
                        onChange={(e) => setMarcaParasitario(e.target.value)}
                        placeholder="Ex: Bravecto, NexGard..."
                        size={isSmallScreen ? "small" : "medium"}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Aplicação"
                          type="date"
                          value={ultimaAplicacao}
                          onChange={(e) => setUltimaAplicacao(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Aplicação")
                          }
                          title="Criar lembrete para próxima aplicação"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Aplicação"
                        type="date"
                        value={proximaAplicacao}
                        onChange={(e) => setProximaAplicacao(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Aplicação"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Tipo de Aplicação"
                        value={tipoAplicacao}
                        onChange={(e) => setTipoAplicacao(e.target.value)}
                        placeholder="Ex: Antibiótico, ..."
                        size={isSmallScreen ? "small" : "medium"}
                      />
                    </Grid>

                    {/* Seção de Vacinas */}
                    <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Histórico de Vacinas
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Vacina Múltipla"
                          type="date"
                          value={ultimaVacinaMultipla}
                          onChange={(e) =>
                            setUltimaVacinaMultipla(e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Vacina Múltipla")
                          }
                          title="Criar lembrete para próxima vacina múltipla"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Vacina Múltipla"
                        type="date"
                        value={proximaVacinaMultipla}
                        onChange={(e) =>
                          setProximaVacinaMultipla(e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Vacina Múltipla"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Vacina Puppy"
                          type="date"
                          value={ultimaVacinaPuppy}
                          onChange={(e) => setUltimaVacinaPuppy(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Vacina Puppy")
                          }
                          title="Criar lembrete para próxima vacina puppy"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Vacina Puppy"
                        type="date"
                        value={proximaVacinaPuppy}
                        onChange={(e) => setProximaVacinaPuppy(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Vacina Puppy"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Vacina Giardia"
                          type="date"
                          value={ultimaVacinaGiardia}
                          onChange={(e) =>
                            setUltimaVacinaGiardia(e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Vacina Giardia")
                          }
                          title="Criar lembrete para próxima vacina de giardia"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Vacina Giardia"
                        type="date"
                        value={proximaVacinaGiardia}
                        onChange={(e) =>
                          setProximaVacinaGiardia(e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Vacina Giardia"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Vacina de Raiva"
                          type="date"
                          value={ultimaVacinaRaiva}
                          onChange={(e) => setUltimaVacinaRaiva(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size={isSmallScreen ? "small" : "medium"}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Vacina Raiva")
                          }
                          title="Criar lembrete para próxima vacina de raiva"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Vacina de Raiva"
                        type="date"
                        value={proximaVacinaRaiva}
                        onChange={(e) => setProximaVacinaRaiva(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Vacina de Raiva"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          fullWidth
                          label="Última Vacina de Gripe"
                          size={isSmallScreen ? "small" : "medium"}
                          type="date"
                          value={ultimaVacinaGripe}
                          onChange={(e) => setUltimaVacinaGripe(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleOpenNotificationModal("Vacina Gripe")
                          }
                          title="Criar lembrete para próxima vacina de gripe"
                          size={isSmallScreen ? "small" : "medium"}
                        >
                          <NotificationsActive />
                        </IconButton>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        label="Próxima Vacina de Gripe"
                        type="date"
                        value={proximaVacinaGripe}
                        onChange={(e) => setProximaVacinaGripe(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isSmallScreen ? "small" : "medium"}
                        helperText={
                          notificationIds["Vacina de Gripe"]
                            ? `Baseada em notificação agendada`
                            : "Nenhuma notificação criada"
                        }
                        disabled
                      />
                    </Grid>

                    {/* Seção de Alimentação */}
                    <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Alimentação
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Primeira Ração"
                        value={primeiraRacao}
                        size={isSmallScreen ? "small" : "medium"}
                        onChange={(e) => setPrimeiraRacao(e.target.value)}
                        placeholder="Ex: Royal Canin Puppy..."
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Quantidade da Ração"
                        value={quantidadeRacao}
                        onChange={(e) => setQuantidadeRacao(e.target.value)}
                        placeholder="Ex: 200g por dia"
                        size={isSmallScreen ? "small" : "medium"}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        )}

        {activeTab === 2 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: isSmallScreen
                ? "calc(100vh - 280px)"
                : isMobile
                  ? "calc(100vh - 350px)"
                  : "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile || isSmallScreen ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile || isSmallScreen ? "flex-start" : "center",
                gap: isMobile || isSmallScreen ? 1 : 0,
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant={isMobile || isSmallScreen ? "subtitle1" : "h6"}
                >
                  Histórico de Compras
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (Categoria: Venda de Produtos)
                </Typography>
              </Box>
              {props.tecnico?.Tecnico && (
                <Button
                  variant="contained"
                  size={isMobile || isSmallScreen ? "small" : "medium"}
                  startIcon={!(isMobile || isSmallScreen) && <Add />}
                  onClick={() => setModalAddVenda(true)}
                  color="success"
                  fullWidth={isMobile || isSmallScreen}
                >
                  {isMobile || isSmallScreen ? "Nova Venda" : "Nova Venda"}
                </Button>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                maxHeight: isSmallScreen
                  ? "calc(100vh - 280px)"
                  : isMobile
                    ? "calc(100vh - 350px)"
                    : 400,
                minHeight: isSmallScreen
                  ? "calc(100vh - 280px)"
                  : isMobile
                    ? "calc(100vh - 350px)"
                    : 400,
              }}
            >
              {loadingPedidos ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando...
                </Typography>
              ) : pedidosCompras.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma compra encontrada para este cliente
                </Typography>
              ) : (
                <List>
                  {pedidosCompras.map((item, index) => {
                    return (
                      <ListItem
                        key={`${item.ID}-${index}`}
                        sx={{
                          py: 2,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        onClick={() => handleAbrirDetalhes(item)}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "success.main",
                            }}
                          >
                            <Store />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <>
                              <Typography variant="body1" fontWeight="medium">
                                {item.Data}
                              </Typography>
                            </>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {`${item.Items?.[0]?.Descricao || "Produto"} - Código: ${item.Codigo}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="primary.main"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Categoria: {item.Categoria}
                              </Typography>
                            </>
                          }
                        />
                        {props.tecnico?.Tecnico && (
                          <IconButton
                            edge="end"
                            aria-label="editar"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAbrirEdicao(item);
                            }}
                            sx={{ ml: 1 }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Paper
            sx={{
              p: isMobile ? 1 : 3,
              borderRadius: 2,
              margin: "auto",
              width: "100%",
            }}
          >
            {/* Aviso quando tutor não tem CPF */}
            {!isEditingTutor &&
              !props.initialData?.tutor?.cpf &&
              !tutorApiData?.CNPJ_CPF && (
                <Alert severity="warning" sx={{ mb: isMobile ? 2 : 3 }}>
                  <strong>Atenção:</strong> Este tutor não possui CPF
                  cadastrado. Para editar as informações, é necessário adicionar
                  um CPF ao cadastro do tutor no sistema ERP.
                </Alert>
              )}

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                gap: isMobile ? 2 : 0,
                mb: isMobile ? 2 : 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? 1 : 2,
                }}
              >
                <Avatar
                  sx={{
                    width: isMobile ? 50 : 80,
                    height: isMobile ? 50 : 80,
                    bgcolor: "primary.main",
                  }}
                >
                  <Person sx={{ fontSize: isMobile ? 30 : 40 }} />
                </Avatar>
                <Box sx={{ overflow: "hidden" }}>
                  <Typography
                    variant={isMobile ? "h6" : "h4"}
                    component="h1"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: isMobile ? "nowrap" : "normal",
                      maxWidth: isMobile ? "200px" : "none",
                    }}
                  >
                    {props.initialData?.tutor.name || "Tutor não informado"}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 1,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {!isEditingTutor ? (
                  <>
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      startIcon={!isMobile && <Edit />}
                      onClick={handleStartEditTutor}
                      disabled={
                        !props.initialData?.tutor?.cpf &&
                        !tutorApiData?.CNPJ_CPF
                      }
                      title={
                        !props.initialData?.tutor?.cpf &&
                        !tutorApiData?.CNPJ_CPF
                          ? "Tutor sem CPF - não é possível editar"
                          : ""
                      }
                      fullWidth={isMobile}
                    >
                      {isMobile ? "Editar" : "Editar Tutor"}
                    </Button>
                    <Button
                      variant="contained"
                      size={isMobile ? "small" : "medium"}
                      startIcon={!isMobile && <Add />}
                      onClick={props.openModalAddPet}
                      fullWidth={isMobile}
                    >
                      {isMobile ? "Adicionar Pet" : "Adicionar Pet"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      onClick={handleCancelEditTutor}
                      disabled={savingTutor}
                      fullWidth={isMobile}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                      onClick={handleSaveTutor}
                      disabled={savingTutor}
                      fullWidth={isMobile}
                    >
                      {savingTutor ? "Salvando..." : "Salvar"}
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: isMobile ? 1 : 2 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  Informações Pessoais
                  {tutorApiData && !isEditingTutor ? (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Dados atualizados da API)
                    </Typography>
                  ) : loadingTutorApi ? (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Buscando dados atualizados...)
                    </Typography>
                  ) : !isEditingTutor &&
                    !tutorApiData &&
                    !loadingTutorApi &&
                    (props.initialData?.tutor?.cpf ||
                      (props.initialData?.tutor as any)?.idERP ||
                      (props.initialData?.tutor as any)?.erpId ||
                      props.initialData?.tutor?.telefone ||
                      props.initialData?.tutor?.celular) ? (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Não encontrado na API)
                    </Typography>
                  ) : !isEditingTutor && !tutorApiData && !loadingTutorApi ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Sem critérios de busca)
                    </Typography>
                  ) : null}
                </Typography>

                {!isEditingTutor ? (
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Nome"
                        secondary={
                          tutorApiData?.RazaoSocial ||
                          tutorApiData?.NomeFantasia ||
                          props.initialData?.tutor.name ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="CPF/CNPJ"
                        secondary={
                          tutorApiData?.CNPJ_CPF ||
                          props.initialData?.tutor.cpf ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Data de Nascimento"
                        secondary={formatDateOnly(tutorApiData?.DataNascimento)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Email Principal"
                        secondary={
                          tutorApiData?.Email ||
                          props.initialData?.tutor.email ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Telefone"
                        secondary={
                          tutorApiData?.Telefone ||
                          props.initialData?.tutor.telefone ||
                          props.initialData?.tutor.celular ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Celular"
                        secondary={
                          tutorApiData?.Celular ||
                          props.initialData?.tutor.celular ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Telefones Secundários"
                        secondary={
                          (tutorApiData?.TelefonesSecundarios &&
                          tutorApiData.TelefonesSecundarios.length > 0
                            ? tutorApiData.TelefonesSecundarios.join(", ")
                            : null) ||
                          props.initialData?.tutor.telefonesSecundarios ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                  </List>
                ) : (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: isSmallScreen ? 1 : 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      required
                      label="Nome"
                      value={editedTutorData.name || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("name", e.target.value)
                      }
                      error={!editedTutorData.name?.trim()}
                      helperText={
                        !editedTutorData.name?.trim()
                          ? "Nome é obrigatório"
                          : ""
                      }
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="CPF/CNPJ"
                      value={editedTutorData.cpf || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("cpf", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Data de Nascimento"
                      type="date"
                      value={editedTutorData.birthDate || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("birthDate", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={editedTutorData.email || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("email", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Telefone"
                      value={editedTutorData.phone || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("phone", e.target.value)
                      }
                      error={
                        !editedTutorData.phone?.trim() &&
                        !editedTutorData.mobile?.trim()
                      }
                      helperText={
                        !editedTutorData.phone?.trim() &&
                        !editedTutorData.mobile?.trim()
                          ? "Pelo menos um telefone (fixo ou celular) é obrigatório"
                          : "Campo obrigatório se não houver celular"
                      }
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Celular"
                      value={editedTutorData.mobile || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("mobile", e.target.value)
                      }
                      error={
                        !editedTutorData.phone?.trim() &&
                        !editedTutorData.mobile?.trim()
                      }
                      helperText={
                        !editedTutorData.phone?.trim() &&
                        !editedTutorData.mobile?.trim()
                          ? "Pelo menos um telefone (fixo ou celular) é obrigatório"
                          : "Campo obrigatório se não houver telefone fixo"
                      }
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Telefones Secundários"
                      value={editedTutorData.secondaryPhone || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("secondaryPhone", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                  </Box>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  Endereço Padrão
                  {tutorApiData && !isEditingTutor ? (
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Dados atualizados da API)
                    </Typography>
                  ) : !isEditingTutor &&
                    !tutorApiData &&
                    !loadingTutorApi &&
                    (props.initialData?.tutor?.cpf ||
                      (props.initialData?.tutor as any)?.idERP ||
                      (props.initialData?.tutor as any)?.erpId ||
                      props.initialData?.tutor?.telefone ||
                      props.initialData?.tutor?.celular) ? (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Não encontrado na API)
                    </Typography>
                  ) : !isEditingTutor && !tutorApiData && !loadingTutorApi ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1, fontStyle: "italic" }}
                    >
                      (Sem critérios de busca)
                    </Typography>
                  ) : null}
                </Typography>

                {!isEditingTutor ? (
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Logradouro"
                        secondary={
                          tutorApiData?.EnderecoPadrao?.Logradouro ||
                          tutorApiData?.Logradouro
                            ? `${
                                tutorApiData?.EnderecoPadrao?.Logradouro ||
                                tutorApiData?.Logradouro
                              }${
                                tutorApiData?.EnderecoPadrao?.Numero ||
                                tutorApiData?.LogradouroNumero
                                  ? `, ${
                                      tutorApiData?.EnderecoPadrao?.Numero ||
                                      tutorApiData?.LogradouroNumero
                                    }`
                                  : ""
                              }`
                            : props.initialData?.tutor.logradouro
                              ? `${props.initialData?.tutor.logradouro}, ${props.initialData?.tutor.logradouroNumero}`
                              : "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Bairro"
                        secondary={
                          tutorApiData?.EnderecoPadrao?.Bairro ||
                          tutorApiData?.Bairro ||
                          props.initialData?.tutor.bairro ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Cidade/UF"
                        secondary={
                          tutorApiData?.EnderecoPadrao?.Cidade &&
                          tutorApiData?.EnderecoPadrao?.Uf
                            ? `${tutorApiData.EnderecoPadrao.Cidade}/${tutorApiData.EnderecoPadrao.Uf}`
                            : tutorApiData?.Cidade && tutorApiData?.UF
                              ? `${tutorApiData.Cidade}/${tutorApiData.UF}`
                              : tutorApiData?.EnderecoPadrao?.Cidade ||
                                tutorApiData?.Cidade ||
                                props.initialData?.tutor.cidade ||
                                "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="CEP"
                        secondary={
                          tutorApiData?.EnderecoPadrao?.CEP ||
                          tutorApiData?.CEP ||
                          props.initialData?.tutor.cep ||
                          "Não informado"
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="País" secondary="Brasil" />
                    </ListItem>
                  </List>
                ) : (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: isSmallScreen ? 1 : 2,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Logradouro"
                      value={editedTutorData.addressStreet || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("addressStreet", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Número"
                      value={editedTutorData.addressNumber || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("addressNumber", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Bairro"
                      value={editedTutorData.addressDistrict || ""}
                      onChange={(e) =>
                        handleTutorFieldChange(
                          "addressDistrict",
                          e.target.value
                        )
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="Cidade"
                      value={editedTutorData.addressCity || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("addressCity", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="UF"
                      value={editedTutorData.addressState || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("addressState", e.target.value)
                      }
                      inputProps={{ maxLength: 2 }}
                      helperText="Campo opcional - Máximo 2 caracteres"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                    <TextField
                      fullWidth
                      label="CEP"
                      value={editedTutorData.addressZip || ""}
                      onChange={(e) =>
                        handleTutorFieldChange("addressZip", e.target.value)
                      }
                      helperText="Campo opcional"
                      size={isSmallScreen ? "small" : "medium"}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {activeTab === 3 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: isSmallScreen
                ? "calc(100vh - 280px)"
                : isMobile
                  ? "calc(100vh - 350px)"
                  : "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile || isSmallScreen ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile || isSmallScreen ? "flex-start" : "center",
                gap: isMobile || isSmallScreen ? 1 : 0,
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant={isMobile || isSmallScreen ? "subtitle1" : "h6"}
                >
                  Histórico de Atendimentos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (Categoria: Venda de Serviços)
                </Typography>
              </Box>
              {props.tecnico?.Tecnico && (
                <Button
                  variant="contained"
                  size={isMobile || isSmallScreen ? "small" : "medium"}
                  startIcon={!(isMobile || isSmallScreen) && <Add />}
                  onClick={() => setModalAddAtendimento(true)}
                  color="warning"
                  fullWidth={isMobile || isSmallScreen}
                >
                  {isMobile || isSmallScreen
                    ? "Novo Atendimento"
                    : "Novo Atendimento"}
                </Button>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                maxHeight: isSmallScreen
                  ? "calc(100vh - 280px)"
                  : isMobile
                    ? "calc(100vh - 350px)"
                    : 400,
                minHeight: isSmallScreen
                  ? "calc(100vh - 280px)"
                  : isMobile
                    ? "calc(100vh - 350px)"
                    : 400,
              }}
            >
              {loadingPedidos ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando...
                </Typography>
              ) : pedidosAtendimentos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhum atendimento encontrado para este cliente
                </Typography>
              ) : (
                <List>
                  {pedidosAtendimentos.map((item, index) => {
                    return (
                      <ListItem
                        key={`${item.ID}-${index}`}
                        sx={{
                          py: 2,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        onClick={() => handleAbrirDetalhes(item)}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "warning.main",
                            }}
                          >
                            <Pets />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <>
                              <Typography variant="body1" fontWeight="medium">
                                {item.Data}
                              </Typography>
                            </>
                          }
                          secondary={
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {`${item.Items?.[0]?.Descricao || "Serviço"} - Código: ${item.Codigo}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Categoria: {item.Categoria}
                              </Typography>
                            </>
                          }
                        />
                        {props.tecnico?.Tecnico && (
                          <IconButton
                            edge="end"
                            aria-label="editar"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAbrirEdicao(item);
                            }}
                            sx={{ ml: 1 }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        )}

        {/* Tab de Arquivos */}
        {((!props.tecnico?.Tecnico && activeTab === 3) ||
          (props.tecnico?.Tecnico && activeTab === 4)) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: isSmallScreen
                ? "calc(100vh - 280px)"
                : isMobile
                  ? "calc(100vh - 350px)"
                  : "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile || isSmallScreen ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile || isSmallScreen ? "flex-start" : "center",
                gap: isMobile || isSmallScreen ? 1 : 0,
                mb: 2,
              }}
            >
              <Typography
                variant={isMobile || isSmallScreen ? "subtitle1" : "h6"}
              >
                Arquivos do Pet
              </Typography>
              <Box sx={{ width: isMobile || isSmallScreen ? "100%" : "auto" }}>
                <input
                  accept="*/*"
                  style={{ display: "none" }}
                  id="file-upload-button"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label
                  htmlFor="file-upload-button"
                  style={{
                    width: isMobile || isSmallScreen ? "100%" : "auto",
                    display: "block",
                  }}
                >
                  <Button
                    variant="contained"
                    component="span"
                    size={isMobile || isSmallScreen ? "small" : "medium"}
                    startIcon={!(isMobile || isSmallScreen) && <CloudUpload />}
                    disabled={uploadingFile}
                    fullWidth={isMobile || isSmallScreen}
                  >
                    {uploadingFile
                      ? "Enviando..."
                      : isMobile || isSmallScreen
                        ? "Adicionar"
                        : "Adicionar Arquivo"}
                  </Button>
                </label>
              </Box>
            </Box>

            {/* Campo de descrição para o arquivo */}
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Descrição do arquivo (opcional)"
                variant="outlined"
                fullWidth
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                size={isMobile || isSmallScreen ? "small" : "medium"}
              />
            </Box>

            {/* Indicador de carregamento */}
            {uploadingFile && <LinearProgress sx={{ mb: 2 }} />}

            {/* Lista de arquivos */}
            {loadingFiles ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <LinearProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  minHeight: isSmallScreen
                    ? "calc(100vh - 400px)"
                    : isMobile
                      ? "calc(100vh - 500px)"
                      : "auto",
                }}
              >
                {petFiles.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: "center" }}>
                    <AttachFile
                      sx={{ fontSize: 48, color: "text.secondary" }}
                    />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Nenhum arquivo encontrado para este pet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Adicione documentos, exames, fotos e outros arquivos
                      importantes
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {petFiles.map((file: any) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={file.id}>
                        <Card>
                          {/* Thumbnail de imagem */}
                          {isImageFile(file.fileType) &&
                            thumbnailUrls[file.id] && (
                              <CardMedia
                                component="img"
                                height="140"
                                image={thumbnailUrls[file.id]}
                                alt={file.originalName}
                                sx={{
                                  cursor: "pointer",
                                  objectFit: "cover",
                                  backgroundColor: "#f5f5f5",
                                }}
                                onClick={() => handleViewFile(file)}
                              />
                            )}

                          {/* Thumbnail de PDF */}
                          {isPdfFile(file.fileType) && (
                            <Box
                              sx={{
                                height: 140,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f44336",
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: "#d32f2f",
                                },
                              }}
                              onClick={() => handleViewFile(file)}
                            >
                              <PictureAsPdf
                                sx={{ fontSize: 60, color: "white" }}
                              />
                            </Box>
                          )}

                          <CardContent sx={{ pb: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              {getFileIcon(file.fileType)}
                              <Typography
                                variant="subtitle2"
                                sx={{ ml: 1, flexGrow: 1 }}
                              >
                                {file.originalName}
                              </Typography>
                            </Box>

                            <Box sx={{ mb: 1 }}>
                              <Chip
                                size="small"
                                label={formatFileSize(file.fileSize)}
                                sx={{ mr: 1 }}
                              />
                              <Chip
                                size="small"
                                label={
                                  file.fileType.split("/")[1]?.toUpperCase() ||
                                  "FILE"
                                }
                                variant="outlined"
                              />
                            </Box>

                            {file.description && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {file.description}
                              </Typography>
                            )}

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Enviado por: {file.uploadedBy?.name || "Usuário"}
                            </Typography>
                            <br />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(file.createdAt).toLocaleDateString(
                                "pt-BR"
                              )}
                            </Typography>
                          </CardContent>

                          <CardActions sx={{ pt: 0 }}>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleViewFile(file)}
                            >
                              Visualizar
                            </Button>
                            <Button
                              size="small"
                              startIcon={<Download />}
                              onClick={() =>
                                handleFileDownload(file.id, file.originalName)
                              }
                            >
                              Baixar
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleFileDelete(file.id)}
                            >
                              Excluir
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {activeTab === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 1 : 2,
            p: isMobile ? 1 : 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            color="error"
            size={isMobile ? "small" : "medium"}
            startIcon={!isMobile && <Delete />}
            onClick={handleOpenDeleteModal}
            fullWidth={isMobile}
          >
            {isMobile ? "Excluir" : "Excluir Pet"}
          </Button>
          <Box
            sx={{
              display: "flex",
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {props.onClose && (
              <Button
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                onClick={() => {
                  props.onSave?.();
                  props.onClose?.();
                }}
                fullWidth={isMobile}
              >
                Cancelar
              </Button>
            )}
            <Button
              variant="contained"
              size={isMobile ? "small" : "medium"}
              onClick={handleSubmit}
              disabled={
                !petData?.name ||
                !petData.gender ||
                !petData.breed ||
                isSubmitting
              }
              fullWidth={isMobile}
            >
              {isSubmitting
                ? "Salvando..."
                : isMobile
                  ? "Salvar"
                  : "Salvar Alterações"}
            </Button>
          </Box>
        </Box>
      )}

      {/* Modal de visualização de arquivos */}
      <Dialog
        open={fileViewModal}
        onClose={() => setFileViewModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">
              {selectedFileForView?.originalName || "Visualizar Arquivo"}
            </Typography>
            <IconButton onClick={() => setFileViewModal(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFileForView && isImageFile(selectedFileForView.fileType) ? (
            <Box sx={{ textAlign: "center" }}>
              <img
                src={selectedFileForView.signedUrl}
                alt={selectedFileForView.originalName}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : selectedFileForView && isPdfFile(selectedFileForView.fileType) ? (
            <Box sx={{ width: "100%", height: "70vh" }}>
              <iframe
                src={selectedFileForView.signedUrl}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title={selectedFileForView.originalName}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Visualização não disponível para este tipo de arquivo.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Use o botão &ldquo;Baixar&rdquo; para fazer o download do
                arquivo.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            {/* Botões de ação à esquerda */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {selectedFileForView &&
                isPdfFile(selectedFileForView.fileType) && (
                  <Button
                    variant="outlined"
                    startIcon={<OpenInNew />}
                    onClick={() => {
                      if (selectedFileForView?.signedUrl) {
                        window.open(selectedFileForView.signedUrl, "_blank");
                      }
                    }}
                  >
                    Abrir em Nova Guia
                  </Button>
                )}

              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => {
                  if (selectedFileForView) {
                    handleFileDownload(
                      selectedFileForView.id,
                      selectedFileForView.originalName
                    );
                  }
                }}
              >
                Baixar
              </Button>
            </Box>

            {/* Botão fechar à direita */}
            <Box sx={{ marginLeft: "auto" }}>
              <Button onClick={() => setFileViewModal(false)}>Fechar</Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      <ModalNewAtendimento
        open={modalAddAtendimento}
        onClose={() => setModalAddAtendimento(false)}
        onSave={async () => {
          setModalAddAtendimento(false);
          // Recarregar dados de atendimentos
          await refetchPedidos();
          // Atualizar lista principal se callback existir
          if (props.onSave) props.onSave();
        }}
        tecnico={props.tecnico}
        initialPet={petData}
      />

      <ModalNewVenda
        open={modalAddVenda}
        onClose={() => setModalAddVenda(false)}
        onSave={async () => {
          setModalAddVenda(false);
          // Recarregar dados de vendas
          await refetchPedidos();
          // Atualizar lista principal se callback existir
          if (props.onSave) props.onSave();
        }}
        tecnico={props.tecnico}
        initialPet={petData}
      />

      <ModalDetalhesPedido
        open={modalDetalhesPedido}
        onClose={handleFecharDetalhes}
        pedido={pedidoSelecionado}
      />

      <ModalEditarPedido
        open={modalEditarPedido}
        onClose={handleFecharEdicao}
        pedido={pedidoSelecionado}
        onSave={async () => {
          handleFecharEdicao();
          // Recarregar dados após edição
          await refetchPedidos();
          // Atualizar lista principal se callback existir
          if (props.onSave) props.onSave();
        }}
      />

      {/* Modal para criar notificação de vacina/vermifugação */}
      <Dialog
        open={notificationModal}
        onClose={handleCloseNotificationModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">
              Criar Lembrete - {notificationType}
            </Typography>
            <IconButton onClick={handleCloseNotificationModal}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Configure o lembrete para a próxima dose de {notificationType} do
              pet <strong>{petData?.name}</strong>.
            </Typography>

            <TextField
              fullWidth
              label="Data da Próxima Dose"
              type="date"
              value={notificationDate}
              onChange={(e) => setNotificationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              label="Horário do Lembrete"
              type="time"
              value={notificationTime}
              onChange={(e) => setNotificationTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            <Typography variant="caption" color="text.secondary">
              Você receberá uma notificação no dia e horário configurados para
              lembrar da próxima dose.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotificationModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateNotification}
            disabled={!notificationDate || creatingNotification}
            startIcon={<NotificationsActive />}
          >
            {creatingNotification ? "Criando..." : "Criar Lembrete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de exclusão */}
      <Dialog
        open={deleteConfirmModal}
        onClose={handleCloseDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" color="error">
              Confirmar Exclusão
            </Typography>
            <IconButton onClick={handleCloseDeleteModal}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body1">
              Tem certeza que deseja excluir o pet{" "}
              <strong>{petData?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação não pode ser desfeita. Todos os dados do pet serão
              permanentemente removidos.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} disabled={deletingPet}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeletePet}
            disabled={deletingPet}
            startIcon={<Delete />}
          >
            {deletingPet ? "Excluindo..." : "Excluir Pet"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PetDetails;
